import isEqual from 'lodash-es/isEqual'
import map from 'lodash-es/map'

import { getQaSelector, isEqualNullables } from '../../utils/common'
import { getHtmlTelLink } from '../../utils/link'
import { updatePaginationPage } from '../../utils/pagination'
import { toCSSPixel } from '../../utils/styles'

import {
  QA_NAME_TABLE_ROW_DEFAULT,
  SCREEN_WIDTH_HIDE_TABLE_CONTROL_BAR_TEXT,
  TABLE_DATE_CELL_NAMES,
  TABLE_NUMBER_CELL_NAMES,
} from './Table.constants'
import { TableContext } from './Table.enums'

export function isBuildingTable(context) {
  return context === TableContext.Building
}

export function isFlatTable(context) {
  return context === TableContext.Flat
}

export function isRequestTable(context) {
  return context === TableContext.Request
}

export function isUserTable(context) {
  return context === TableContext.User
}

export function isNumberCell(name) {
  return TABLE_NUMBER_CELL_NAMES.includes(name)
}

export function isDateCell(name) {
  return TABLE_DATE_CELL_NAMES.includes(name)
}

export function getTableRowKey(item) {
  return item.id || item.uuid
}

export function getTableRowQaProps(qaName) {
  return getQaSelector({
    context: QA_NAME_TABLE_ROW_DEFAULT,
    def: QA_NAME_TABLE_ROW_DEFAULT,
    value: qaName,
  })
}

export function getTableWrapperStyles(scrollButtonY) {
  return {
    '--table-scroll-button-y': toCSSPixel(scrollButtonY),
  }
}

export function loadTableData(
  { handleLoadPage, handleLoadPageInitial, isLoadingPage, orderRef, paramsRef },
  params,
  pagination = null,
  order = null
) {
  const { current: currParams } = paramsRef
  const { current: currOrder } = orderRef

  // Compare `params` by value (deep), like `order` below: it wraps a nested
  // `filters` object built from an inline, non-memoizable `extraFilters`, so its
  // reference changes every render. A shallow compare refetched on each render
  // (infinite loop on search pages).
  if (!isEqualNullables(params, currParams, isEqual)) {
    return handleLoadPageInitial(
      params,
      currParams == null ? pagination : updatePaginationPage(pagination, 1),
      order
    )
  } else if (!isEqualNullables(order, currOrder, isEqual)) {
    return handleLoadPage(pagination, order)
  }

  // Nothing changed: settle so the caller's `.then` can mark the table
  // initiated. While a load is in flight return null instead — settling early
  // would make the pending initial load finish as a refetch.
  return isLoadingPage ? null : Promise.resolve()
}

export function toTableCellMultiValue(data) {
  return {
    children: data,
  }
}

export function toPhoneCellMultiValue(data) {
  return {
    ...toTableCellMultiValue(data),
    to: getHtmlTelLink(data),
  }
}

export function toTableCellTextFromName(data) {
  return {
    ...toTableCellMultiValue(data.name),
    key: data.id,
  }
}

/**
 * If ordering is empty then return `null` and component won't render at all.
 * Otherwise return `undefined` and parent component with buttons will take default reset button component.
 *
 * @param ordering
 * @returns null | undefined
 */
export function getTableResetSortingButton(ordering) {
  return ordering?.length === 0 ? null : undefined
}

export function getTableVisibleColumns(columns) {
  return (
    columns &&
    Object.values(columns)
      .sort((a, b) => {
        return a.index < b.index ? -1 : 1
      })
      .filter(i => i.visible)
  )
}

export function getVisibleColumnsWidth(columns) {
  return Object.values(columns)
    .sort((a, b) => {
      return a.index < b.index ? -1 : 1
    })
    .reduce((acc, item) => {
      if (item.visible) {
        return acc + item.width
      }

      return acc
    }, 0)
}

export function getSelectedSize({
  exclude,
  pagination,
  selectAllPages,
  selected,
}) {
  return selectAllPages ? pagination.count - exclude.length : selected.length
}

export function getDropResultForUpdateRowsPosition({
  activeId,
  items,
  overId,
}) {
  if (!activeId || !overId || String(activeId) === String(overId)) {
    return
  }

  const sourceIndex = items.findIndex(i => String(i.id) === String(activeId))
  const destinationIndex = items.findIndex(i => String(i.id) === String(overId))

  if (sourceIndex < 0 || destinationIndex < 0) {
    return
  }

  return {
    destination: { droppableId: 'droppable', index: destinationIndex },
    draggableId: String(activeId),
    source: { droppableId: 'droppable', index: sourceIndex },
  }
}

export function getUpdatedDraggedRowsPosition({
  itemPositionKey = 'index',
  result,
  rows,
}) {
  if (!result.destination || result.source.index === result.destination.index) {
    return
  }

  return map(rows, row => {
    if (row[itemPositionKey] === result.source.index) {
      return { ...row, [itemPositionKey]: result.destination.index }
    }

    if (
      row[itemPositionKey] >= result.destination.index &&
      row[itemPositionKey] < result.source.index &&
      result.source.index > result.destination.index
    ) {
      return { ...row, [itemPositionKey]: row[itemPositionKey] + 1 }
    }

    if (
      row[itemPositionKey] <= result.destination.index &&
      row[itemPositionKey] > result.source.index &&
      result.source.index < result.destination.index
    ) {
      return { ...row, [itemPositionKey]: row[itemPositionKey] - 1 }
    }

    return row
  })
}

export function removeTableScrollEmptySpace({ scrollElement, tableElement }) {
  if (!scrollElement) {
    return
  }

  const scrollRect = scrollElement.getBoundingClientRect()
  const tableRect = tableElement.getBoundingClientRect()

  const gap = scrollRect.right - tableRect.right

  if (gap > 0) {
    scrollElement.scrollLeft = Math.max(0, scrollElement.scrollLeft - gap)
  }
}

export function getIsHiddenTableControlBarText({
  responsive = false,
  screenWidth,
}) {
  return responsive && screenWidth <= SCREEN_WIDTH_HIDE_TABLE_CONTROL_BAR_TEXT
}
